import { FaPause, FaPlay } from "react-icons/fa6";
import {
  VolumeHigh,
  VolumeSlash,
  Forward10Seconds,
  Backward10Seconds,
} from "iconsax-reactjs";
import { PiSubtitles, PiSubtitlesSlash } from "react-icons/pi";

interface MediaControlsProps {
  isPlaying: boolean;
  isLoading: boolean;
  volume: number;
  playbackRate: number;
  controlsDisabled: boolean;
  hideSkipControls?: boolean;
  showCaption?: boolean;
  captionsEnabled?: boolean;
  playbackRates?: number[];
  onPlayPause: () => void;
  onVolumeToggle: () => void;
  onSkipBackward: () => void;
  onSkipForward: () => void;
  onPlaybackRateChange: () => void;
  onSubtitleToggle?: () => void;
  className?: string;
  buttonColor?: string;
  playButtonColor?: string;
  textDirection?: "ltr" | "rtl";
}

const MediaControls = ({
  isPlaying,
  isLoading,
  volume,
  playbackRate,
  controlsDisabled,
  hideSkipControls = false,
  showCaption = false,
  captionsEnabled = false,
  playbackRates = [1, 1.25, 1.5],
  onPlayPause,
  onVolumeToggle,
  onSkipBackward,
  onSkipForward,
  onPlaybackRateChange,
  onSubtitleToggle,
  className = "",
  buttonColor = "text-primary",
  playButtonColor = "text-white",
  textDirection = "ltr",
}: MediaControlsProps) => {
  return (
    <div
      className={`flex items-center justify-center gap-6 media-player-controls ${className}`}
      style={{ direction: textDirection }}
    >
      {/* Volume */}
      <button
        className={`disabled:opacity-40 ${buttonColor}`}
        onClick={onVolumeToggle}
        disabled={controlsDisabled}
        aria-label={volume > 0 ? "Mute" : "Unmute"}
      >
        {volume > 0 ? (
          <VolumeHigh className="w-6 h-6" />
        ) : (
          <VolumeSlash className="w-6 h-6" />
        )}
      </button>

      {/* Skip Backward 10s */}
      {!hideSkipControls && (
        <button
          onClick={onSkipBackward}
          className={`disabled:opacity-40 ${buttonColor}`}
          disabled={controlsDisabled}
          aria-label="Skip backward 10 seconds"
        >
          <Backward10Seconds className="w-6 h-6" />
        </button>
      )}

      {/* Play/Pause Button */}
      <button
        onClick={onPlayPause}
        className={`w-9 h-9 rounded-full flex items-center justify-center ${playButtonColor} transition-colors disabled:opacity-50 ${
          controlsDisabled
            ? "bg-gray cursor-not-allowed"
            : "bg-primary hover:bg-[#9A9A9A]"
        }`}
        disabled={controlsDisabled || isLoading}
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {isLoading ? (
          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : isPlaying ? (
          <FaPause className="w-3.5 h-3.5" />
        ) : (
          <FaPlay className="w-3.5 h-3.5 ml-0.5" />
        )}
      </button>

      {/* Skip Forward 10s */}
      {!hideSkipControls && (
        <button
          onClick={onSkipForward}
          className={`disabled:opacity-40 ${buttonColor}`}
          disabled={controlsDisabled}
          aria-label="Skip forward 10 seconds"
        >
          <Forward10Seconds className="w-6 h-6" />
        </button>
      )}

      {/* Playback Speed */}
      {!hideSkipControls && playbackRates.length > 0 && (
        <button
          onClick={onPlaybackRateChange}
          className={`disabled:opacity-40 ${buttonColor} font-medium text-body`}
          disabled={controlsDisabled}
          aria-label="Change playback speed"
        >
          {playbackRate}x
        </button>
      )}

      {/* Subtitle */}
      {showCaption && (
        <button
          onClick={onSubtitleToggle}
          className={`disabled:opacity-40 ${buttonColor}`}
          disabled={controlsDisabled}
          aria-label="Toggle subtitles"
        >
          {captionsEnabled ? (
            <PiSubtitles className="w-6 h-6" />
          ) : (
            <PiSubtitlesSlash className="w-6 h-6" />
          )}
        </button>
      )}
    </div>
  );
};

export default MediaControls;
